import React, { useState, useEffect, useMemo } from 'react';
import { 
  LayoutDashboard, 
  Users, 
  MapPin, 
  Calendar, 
  FileText, 
  Menu, 
  X, 
  Bell, 
  Plus,
  StickyNote,
  Briefcase,
  Receipt,
  Settings as SettingsIcon,
  MessageSquareText,
  LogOut,
  Loader2,
  UserCheck,
  ClipboardList,
  RefreshCw,
  Database,
  Map
} from 'lucide-react';
import { ViewType, Contact, Location, Inspection, Agreement, AgreementTemplate, ContactClassification, Note, NoteCategory, Service, Invoice, BillingSettings, Discount, BusinessProfile, InspectionNarrative, ImageTag, Inspector, Availability, ReportTemplate, InspectionReport, NarrativeTag, NarrativeCategory } from './types';
import { supabase, toCamelCase, getErrorMessage, toSnakeCase } from './lib/supabase';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import Contacts from './components/Contacts';
import Locations from './components/Locations';
import Inspections from './components/Inspections';
import RoutePlanner from './components/RoutePlanner';
import Agreements from './components/Agreements';
import SigningPortal from './components/SigningPortal';
import InspectionPortal from './components/InspectionPortal';
import Notes from './components/Notes';
import Services from './components/Services';
import Invoices from './components/Invoices';
import SettingsView from './components/Settings';
import InspectionNarratives from './components/InspectionNarratives';
import Inspectors from './components/Inspectors';
import ReportTemplates from './components/ReportTemplates';
import ReportEditor from './components/ReportEditor';

const INITIAL_BUSINESS_PROFILE: BusinessProfile = {
  appName: 'VIS APP',
  companyName: 'Vista Inspection Services',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  tagline: 'Professional Property Verification',
  website: '',
  defaultTaxRate: 0
};

const DEFAULT_BILLING_SETTINGS: BillingSettings = {
  defaultTaxRate: 0,
  defaultSqftRate: 0.05,
  defaultSqftThreshold: 2000,
  defaultDistanceRate: 1.00,
  defaultDistanceThreshold: 30,
  defaultBasementFee: 50,
  defaultCrawlspaceFee: 75,
  predefinedDiscounts: []
};

const App: React.FC = () => {
  const routeState = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname.split('/').filter(Boolean);
    
    let portalId = params.get('portal');
    let signId = params.get('sign');

    if (pathname.length > 0) {
      if (pathname[0] === 'sign' && pathname[1]) {
        signId = pathname[1];
      } else if (pathname[0] === 'portal' && pathname[1]) {
        portalId = pathname[1];
      }
    }
    return { 
      isPortal: !!(portalId || signId),
      inspectionPortalId: portalId,
      agreementPortalId: signId
    };
  }, []);

  const [session, setSession] = useState<any>(null);
  const [activeView, setActiveView] = useState<ViewType>('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 1280);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  const [activeReportId, setActiveReportId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStep, setSyncStep] = useState('');
  
  const [classifications, setClassifications] = useState<ContactClassification[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [categories, setCategories] = useState<NoteCategory[]>([]);
  const [imageTags, setImageTags] = useState<ImageTag[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);
  const [inspectionReports, setInspectionReports] = useState<InspectionReport[]>([]);
  const [narratives, setNarratives] = useState<InspectionNarrative[]>([]);
  const [narrativeTags, setNarrativeTags] = useState<NarrativeTag[]>([]);
  const [narrativeCategories, setNarrativeCategories] = useState<NarrativeCategory[]>([]);
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile>(INITIAL_BUSINESS_PROFILE);
  const [billingSettings, setBillingSettings] = useState<BillingSettings>(DEFAULT_BILLING_SETTINGS);

  const [invoiceDraftId, setInvoiceDraftId] = useState<string | null>(null);
  const [locationNavigationId, setLocationNavigationId] = useState<string | null>(null);
  const [inspectionNavigationId, setInspectionNavigationId] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (window.innerWidth > 1280) setIsSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: activeSession } }) => {
      setSession(activeSession);
      if (activeSession || routeState.isPortal) {
        fetchAllData(activeSession, true);
      } else {
        setIsInitializing(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession && !isSyncing) fetchAllData(newSession, false);
    });

    return () => subscription.unsubscribe();
  }, [routeState]);

  const fetchAllData = async (currentSession: any, isInitialLoad: boolean) => {
    if (isInitialLoad) setIsInitializing(true);
    setIsSyncing(true);
    try {
      setSyncStep('Identity Registry...');
      const { data: profile } = await supabase.from('business_profiles').select('*').maybeSingle();
      if (profile) setBusinessProfile(toCamelCase(profile));

      setSyncStep('Technical Snapshots...');
      const { data: rpts } = await supabase.from('inspection_reports').select('*');
      if (rpts) setInspectionReports(rpts.map(toCamelCase));

      if (currentSession) {
        setSyncStep('Stakeholders...');
        const { data: cts } = await supabase.from('contacts').select('*');
        if (cts) setContacts(cts.map(toCamelCase));

        const { data: cClasses } = await supabase.from('contact_classifications').select('*');
        if (cClasses) setClassifications(cClasses.map(toCamelCase));

        setSyncStep('Asset Registry...');
        const { data: locs } = await supabase.from('locations').select('*');
        if (locs) {
          const hydratedLocs = await Promise.all(locs.map(async (l) => {
            const { data: joints } = await supabase.from('location_contacts').select('contact_id').eq('location_id', l.id);
            return { ...toCamelCase(l), contactIds: joints?.map(j => j.contact_id) || [] };
          }));
          setLocations(hydratedLocs);
        }

        setSyncStep('Field Schedules...');
        const { data: insps } = await supabase.from('inspections').select('*').order('date', { ascending: true }).order('route_index', { ascending: true });
        if (insps) {
           const hydratedInsps = await Promise.all(insps.map(async (i) => {
             const { data: imgs } = await supabase.from('inspection_images').select('*').eq('inspection_id', i.id);
             return { ...toCamelCase(i), images: (imgs || []).map(toCamelCase) };
           }));
           setInspections(hydratedInsps);
        }

        setSyncStep('Technical Blueprints...');
        const { data: rptTemplates } = await supabase.from('report_templates').select('*');
        if (rptTemplates) setReportTemplates(rptTemplates.map(toCamelCase));

        setSyncStep('Compliance Vault...');
        const { data: agrs } = await supabase.from('agreements').select('*');
        if (agrs) setAgreements(agrs.map(toCamelCase));

        setSyncStep('Logic Registry...');
        const { data: nars } = await supabase.from('inspection_narratives').select('*');
        if (nars) setNarratives(nars.map(toCamelCase));

        const { data: nTags } = await supabase.from('narrative_tags').select('*');
        if (nTags) setNarrativeTags(nTags.map(toCamelCase));

        const { data: nCats } = await supabase.from('narrative_categories').select('*');
        if (nCats) setNarrativeCategories(nCats.map(toCamelCase));

        setSyncStep('Knowledge Base...');
        const { data: nCatsNote } = await supabase.from('note_categories').select('*');
        if (nCatsNote) setCategories(nCatsNote.map(toCamelCase));

        const { data: allNotes } = await supabase.from('notes').select('*');
        if (allNotes) setNotes(allNotes.map(toCamelCase));

        setSyncStep('Financial Ledger...');
        const { data: invs } = await supabase.from('invoices').select('*');
        if (invs) setInvoices(invs.map(toCamelCase));

        const { data: srvs } = await supabase.from('services').select('*');
        if (srvs) setServices(srvs.map(toCamelCase));
        
        const { data: insptrs } = await supabase.from('inspectors').select('*');
        if (insptrs) setInspectors(insptrs.map(toCamelCase));
      }
    } catch (error) {
      console.error("Master Sync Failure:", error);
    } finally {
      setIsInitializing(false);
      setIsSyncing(false);
    }
  };

  const handleSignOut = async () => await supabase.auth.signOut();
  const navigateToBillingWithInspection = (id: string) => { setInvoiceDraftId(id); setActiveView('invoices'); };
  const navigateToLocation = (id: string) => { setLocationNavigationId(id); setActiveView('locations'); };
  const navigateToInspection = (id: string) => { setInspectionNavigationId(id); setActiveView('inspections'); };

  if (isInitializing) {
    return (
      <div className="h-screen w-full bg-[#f8faf7] flex flex-col items-center justify-center p-8 space-y-8">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-[#606c38]/10 border-t-[#606c38] rounded-full animate-spin"></div>
          <Database className="absolute inset-0 m-auto text-[#606c38] animate-pulse" size={20} />
        </div>
        <div className="text-center space-y-2">
          <h1 className="text-lg font-black text-[#283618] uppercase tracking-[0.3em]">{businessProfile.appName || 'VIS HUB'}</h1>
          <p className="text-[#606c38] font-bold text-[9px] uppercase tracking-widest">{syncStep}</p>
        </div>
      </div>
    );
  }

  if (routeState.inspectionPortalId) return <InspectionPortal inspectionId={routeState.inspectionPortalId} businessProfile={businessProfile} onComplete={() => window.location.href = '/'} />;
  if (routeState.agreementPortalId) return <SigningPortal agreementId={routeState.agreementPortalId} businessProfile={businessProfile} onComplete={() => window.location.href = '/'} />;

  if (activeReportId && session) {
    const report = inspectionReports.find(r => r.id === activeReportId);
    const inspection = inspections.find(i => i.id === report?.inspectionId);
    if (report && inspection) {
       return <ReportEditor 
         inspection={inspection} 
         report={report}
         property={locations.find(l => l.id === inspection.propertyId)!}
         templates={reportTemplates}
         reports={inspectionReports}
         setReports={setInspectionReports}
         narratives={narratives}
         tags={narrativeTags}
         onClose={() => setActiveReportId(null)}
       />;
    }
  }

  if (!session) return <Auth />;
  
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'planner', label: 'Route Planner', icon: Map },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'inspections', label: 'Inspections', icon: Calendar },
    { id: 'report_templates', label: 'Templates', icon: ClipboardList },
    { id: 'narratives', label: 'Narratives', icon: MessageSquareText },
    { id: 'agreements', label: 'Agreements', icon: FileText },
    { id: 'services', label: 'Services', icon: Briefcase },
    { id: 'invoices', label: 'Billing', icon: Receipt },
    { id: 'notes', label: 'Notes', icon: StickyNote },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div className="flex h-screen bg-[#f8faf7] overflow-hidden font-sans relative">
      {isMobile && isSidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60]" onClick={() => setIsSidebarOpen(false)} />
      )}

      <aside className={`
        ${isSidebarOpen ? 'w-80' : 'w-0 lg:w-16'} 
        bg-white border-r border-slate-200 transition-all duration-300 flex flex-col z-[70]
        ${isMobile ? 'fixed inset-y-0 left-0' : 'relative'}
      `}>
        <div className="p-6 flex items-center justify-between border-b border-slate-100 h-20 shrink-0">
          <div className={`flex items-center space-x-3 ${(!isSidebarOpen && !isMobile) ? 'hidden' : ''}`}>
            <div className="bg-[#606c38] rounded-xl p-2 shrink-0 shadow-lg shadow-[#606c38]/10"><LayoutDashboard size={20} className="text-white" /></div>
            <span className="font-black text-base uppercase tracking-tight truncate text-[#283618]">{businessProfile.appName}</span>
          </div>
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-slate-50 rounded-xl text-slate-400">
            {(isSidebarOpen || isMobile) ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <nav className="flex-1 mt-6 px-3 space-y-1 overflow-y-auto no-scrollbar">
          {menuItems.map((item) => (
            <button key={item.id} onClick={() => { setActiveView(item.id as ViewType); if (isMobile) setIsSidebarOpen(false); }} className={`w-full flex items-center p-3.5 rounded-xl transition-all ${activeView === item.id ? 'bg-[#606c38] text-white shadow-lg shadow-[#606c38]/20' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}>
              <item.icon size={22} className="shrink-0" />
              {(isSidebarOpen || isMobile) && <span className="ml-4 font-bold text-sm tracking-tight uppercase">{item.label}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-100 shrink-0">
          {(isSidebarOpen || isMobile) && (
            <button onClick={handleSignOut} className="w-full flex items-center gap-4 p-3.5 rounded-xl text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-all text-[10px] font-black uppercase tracking-widest"><LogOut size={18} /> <span>Terminate Session</span></button>
          )}
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-6 lg:px-10 shrink-0 z-40">
          <div className="flex items-center gap-4">
            {isMobile && <button onClick={() => setIsSidebarOpen(true)} className="p-2 -ml-2 text-slate-400 hover:bg-slate-50 rounded-lg"><Menu size={24} /></button>}
            <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] truncate">{activeView} / Protocol Hub</h2>
          </div>
          <div className="flex items-center space-x-6">
            {isSyncing && <div className="flex items-center gap-2 text-[9px] font-black text-[#606c38] uppercase tracking-tighter animate-pulse bg-[#606c38]/5 px-3 py-1.5 rounded-full"><RefreshCw size={10} className="animate-spin" /> Synchronizing</div>}
            <button className="relative p-2.5 text-slate-400 hover:bg-slate-50 rounded-xl transition-all border border-transparent hover:border-slate-100"><Bell size={20} /></button>
            <button onClick={() => navigateToInspection('')} className="bg-slate-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center space-x-2 shadow-xl">
              <Plus size={16} /> <span className="hidden sm:inline">New Audit Log</span>
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 lg:p-12 no-scrollbar bg-[#f8faf7]">
          <div className="max-w-full mx-auto h-full">
            {activeView === 'dashboard' && <Dashboard inspections={inspections} contacts={contacts} invoices={invoices} locations={locations} onNavigateToInspection={navigateToInspection} />}
            {activeView === 'planner' && <RoutePlanner inspections={inspections} setInspections={setInspections} locations={locations} onNavigateToInspection={navigateToInspection} />}
            {activeView === 'contacts' && <Contacts contacts={contacts} setContacts={setContacts} locations={locations} inspections={inspections} notes={notes} setNotes={setNotes} categories={categories} classifications={classifications} setClassifications={setClassifications} onNavigateToLocation={navigateToLocation} />}
            {activeView === 'locations' && <Locations locations={locations} setLocations={setLocations} contacts={contacts} notes={notes} setNotes={setNotes} categories={categories} inspections={inspections} initialLocationId={locationNavigationId} onClearNavigation={() => setLocationNavigationId(null)} onNavigateToInspection={navigateToInspection} />}
            {activeView === 'inspections' && <Inspections inspections={inspections} setInspections={setInspections} properties={locations} contacts={contacts} notes={notes} setNotes={setNotes} categories={categories} invoices={invoices} setInvoices={setInvoices} onCreateInvoice={navigateToBillingWithInspection} onNavigateToLocation={navigateToLocation} initialInspectionId={inspectionNavigationId} onClearNavigation={() => setLocationNavigationId(null)} services={services} agreements={agreements} setAgreements={setAgreements} imageTags={imageTags} setImageTags={setImageTags} businessProfile={businessProfile} inspectors={inspectors} availabilities={[]} onOpenReportEditor={(id) => setActiveReportId(id)} reports={inspectionReports} setReports={setInspectionReports} />}
            {activeView === 'report_templates' && <ReportTemplates templates={reportTemplates} setTemplates={setReportTemplates} services={services} />}
            {activeView === 'agreements' && <Agreements agreements={agreements} setAgreements={setAgreements} inspections={inspections} properties={locations} contacts={contacts} services={services} invoices={invoices} />}
            {activeView === 'inspectors' && <Inspectors inspectors={inspectors} setInspectors={setInspectors} availabilities={[]} setAvailabilities={()=>{}} />}
            {activeView === 'notes' && <Notes notes={notes} setNotes={setNotes} categories={categories} setCategories={setCategories} contacts={contacts} locations={locations} inspections={inspections} />}
            {activeView === 'services' && <Services services={services} setServices={setServices} />}
            {activeView === 'invoices' && <Invoices invoices={invoices} setInvoices={setInvoices} services={services} inspections={inspections} setInspections={setInspections} contacts={contacts} locations={locations} initialInspectionId={invoiceDraftId} onClearDraft={() => setInvoiceDraftId(null)} billingSettings={billingSettings} setBillingSettings={setBillingSettings} businessProfile={businessProfile} notes={notes} setNotes={setNotes} categories={categories} />}
            {activeView === 'narratives' && <InspectionNarratives narratives={narratives} setNarratives={setNarratives} services={services} />}
            {activeView === 'settings' && <SettingsView businessProfile={businessProfile} setBusinessProfile={setBusinessProfile} />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;